import { useState, useEffect } from "react";
import { BarChart3, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../utils/apiClient";
import { exportToXlsx } from "../materials/exportXlsx";

const WORKING_DAYS_PER_MONTH = 26;
const HOURS_PER_DAY = 8;
const AVAILABLE_HOURS = WORKING_DAYS_PER_MONTH * HOURS_PER_DAY; // 208 hours

const UtilizationReportTab = ({ projectId, equipment }) => {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [year, mon] = month.split("-").map(Number);
      const startDate = `${year}-${String(mon).padStart(2, "0")}-01`;
      const endDate = new Date(year, mon, 0).toISOString().split("T")[0];

      const logsRes = await apiClient.get(`/equipment/daily-log/${projectId}`);
      const logs = logsRes.data.filter(l => l.date >= startDate && l.date <= endDate);

      const eqMap = Object.fromEntries(equipment.map(e => [e.id, e]));
      const utilizationMap = {};

      logs.forEach(l => {
        if (!utilizationMap[l.equipment_id]) {
          const eq = eqMap[l.equipment_id];
          utilizationMap[l.equipment_id] = {
            equipment_id: l.equipment_id,
            name: eq?.name || "Unknown",
            reg: eq?.equipment_id || "",
            type: eq?.type || "",
            total_hours: 0,
            working_days: new Set(),
            logs_count: 0
          };
        }
        utilizationMap[l.equipment_id].total_hours += l.hours_used || 0;
        utilizationMap[l.equipment_id].working_days.add(l.date);
        utilizationMap[l.equipment_id].logs_count++;
      });

      const results = Object.values(utilizationMap).map(u => ({
        ...u,
        working_days: u.working_days.size,
        available_hours: AVAILABLE_HOURS,
        utilization_pct: Math.min(100, (u.total_hours / AVAILABLE_HOURS) * 100),
        avg_hours_per_day: u.working_days.size > 0 ? u.total_hours / u.working_days.size : 0
      }));

      // Add equipment with no logs
      equipment.filter(eq => eq.status !== "Archived" && !utilizationMap[eq.id]).forEach(eq => {
        results.push({
          equipment_id: eq.id, name: eq.name, reg: eq.equipment_id, type: eq.type,
          total_hours: 0, working_days: 0, logs_count: 0,
          available_hours: AVAILABLE_HOURS, utilization_pct: 0, avg_hours_per_day: 0
        });
      });

      results.sort((a, b) => b.utilization_pct - a.utilization_pct);
      setData(results);
    } catch (e) {
      toast.error("Failed to fetch utilization data");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (equipment.length > 0) fetchData();
  }, [month, equipment.length]);

  const handleExport = async () => {
    if (data.length === 0) return toast.error("No data to export");
    await exportToXlsx({
      fileName: `Equipment_Utilization_${month}.xlsx`,
      title: "Equipment Utilization Report",
      subtitle: `Month: ${month}  |  Available Hours: ${AVAILABLE_HOURS} (${WORKING_DAYS_PER_MONTH} days × ${HOURS_PER_DAY} hrs)`,
      sheetName: "Utilization",
      columns: [
        { header: "Equipment", key: "name", width: 24 },
        { header: "Reg/ID", key: "reg", width: 14 },
        { header: "Type", key: "type", width: 16 },
        { header: "Working Days", key: "working_days", width: 14 },
        { header: "Total Hours", key: "total_hours", width: 12, numFmt: "#,##0.0", total: true },
        { header: "Available Hrs", key: "available_hours", width: 14 },
        { header: "Utilization %", key: "utilization_pct", width: 14, numFmt: "#,##0.0" },
        { header: "Avg Hrs/Day", key: "avg_hours_per_day", width: 12, numFmt: "#,##0.1" },
        { header: "Log Entries", key: "logs_count", width: 12 }
      ],
      rows: data
    });
    toast.success("Utilization report exported");
  };

  const getUtilColor = (pct) => {
    if (pct >= 80) return "bg-green-500";
    if (pct >= 50) return "bg-blue-500";
    if (pct >= 25) return "bg-amber-500";
    return "bg-slate-300";
  };

  const avgUtil = data.length > 0 ? data.reduce((s, d) => s + d.utilization_pct, 0) / data.length : 0;

  return (
    <div className="space-y-4" data-testid="utilization-report-tab">
      <div className="stat-card">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">Month</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="utilization-month" />
          </div>
          <button onClick={fetchData} disabled={loading} className="btn-primary flex items-center gap-2" data-testid="utilization-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Loading..." : "Refresh"}
          </button>
          <button onClick={handleExport} disabled={data.length === 0} className="btn-outline flex items-center gap-2" data-testid="utilization-export-btn">
            <Download className="w-4 h-4" /> Export Excel
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="p-3 bg-slate-100 rounded-sm">
            <p className="text-xs text-slate-500 uppercase tracking-widest">Total Equipment</p>
            <p className="text-2xl font-bold font-mono">{data.length}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-sm">
            <p className="text-xs text-green-600 uppercase tracking-widest">High Utilization (≥80%)</p>
            <p className="text-2xl font-bold font-mono text-green-700">{data.filter(d => d.utilization_pct >= 80).length}</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-sm">
            <p className="text-xs text-amber-600 uppercase tracking-widest">Low Utilization (&lt;25%)</p>
            <p className="text-2xl font-bold font-mono text-amber-700">{data.filter(d => d.utilization_pct < 25).length}</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-sm">
            <p className="text-xs text-blue-600 uppercase tracking-widest">Avg Utilization</p>
            <p className="text-2xl font-bold font-mono text-blue-700">{avgUtil.toFixed(1)}%</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="utilization-table">
            <thead>
              <tr className="border-b-2 border-slate-300 text-[10px] uppercase tracking-widest text-slate-600">
                <th className="py-2 px-3 text-left">Equipment</th>
                <th className="py-2 px-3 text-left">Type</th>
                <th className="py-2 px-3 text-right">Days</th>
                <th className="py-2 px-3 text-right">Hours</th>
                <th className="py-2 px-3 text-right">Avg/Day</th>
                <th className="py-2 px-3 w-[200px]">Utilization</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.equipment_id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`util-row-${d.equipment_id}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium text-primary">{d.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{d.reg}</div>
                  </td>
                  <td className="py-2 px-3 text-slate-600">{d.type}</td>
                  <td className="py-2 px-3 text-right font-mono">{d.working_days}</td>
                  <td className="py-2 px-3 text-right font-mono font-medium">{d.total_hours.toFixed(1)}</td>
                  <td className="py-2 px-3 text-right font-mono">{d.avg_hours_per_day.toFixed(1)}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-4 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full ${getUtilColor(d.utilization_pct)} transition-all`} style={{ width: `${d.utilization_pct}%` }} />
                      </div>
                      <span className={`font-mono font-bold text-xs w-12 text-right ${d.utilization_pct >= 80 ? "text-green-600" : d.utilization_pct < 25 ? "text-amber-600" : "text-slate-600"}`}>
                        {d.utilization_pct.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.length === 0 && !loading && (
          <div className="text-center py-8 text-slate-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-2 text-slate-400" />
            No utilization data for the selected month.
          </div>
        )}
      </div>
    </div>
  );
};

export default UtilizationReportTab;
